import { forwardRef, useState } from 'react';
import DefinitionTooltip from '../DefinitionTooltip';
import { FiChevronDown } from 'react-icons/fi';
import { IconButton } from '../Button';
import { Listbox } from '@headlessui/react';
import { MenuItem } from '../Button/MenuButton';
import SortSelect from '../Input/SortSelect';
import { getMonthRange, getWeekRange } from './utils';
import useTableColumns from 'utils/hooks/useTableColumns';

/**
 * `rankType` is the time window alone, so it is source-neutral. Two
 * source-specific strings hang off it and both leave the app:
 * `spec.metricKeys[rankType]` goes to the API as `rank_type` and enters
 * queryKey[1] (utils/hooks/api.jsx), and `spec.eventRankTypes[rankType]` is
 * the Mixpanel `Change` property that reports segment on.
 *
 * The two are currently the same string in different case. Keep them declared
 * separately: renaming an API key must not silently repoint an analytics
 * property.
 */
export const RANK = {
    LAST_WEEK: 'LAST_WEEK',
    AVG_WEEKLY: 'AVG_WEEKLY',
    TOTAL_POST: 'TOTAL_POST',
};

function MaybeTooltipOption({
    children,
    week,
    year,
    tooltip,
    mixpanelContentKey,
}) {
    if (!(tooltip && mixpanelContentKey)) return children;
    return (
        <DefinitionTooltip
            {...tooltip}
            week={week}
            year={year}
            mixpanelMeta={{ contentKey: mixpanelContentKey }}
        >
            <div>{children}</div>
        </DefinitionTooltip>
    );
}

const SelectOptionWrapper = MenuItem;

const SelectOption = forwardRef(
    (
        {
            active,
            selected,
            option,
            mixpanelContentKey,
            format: _format,
            ...props
        },
        ref,
    ) => (
        <MaybeTooltipOption
            week={option.week}
            year={option.year}
            tooltip={option.tooltip}
            mixpanelContentKey={mixpanelContentKey}
        >
            <SelectOptionWrapper
                active={active}
                selected={selected}
                className={active || selected ? 'active' : ''}
                {...props}
                ref={ref}
            >
                {option.label}
            </SelectOptionWrapper>
        </MaybeTooltipOption>
    ),
);

const SelectButton = forwardRef(({ value, ...props }, ref) => {
    return (
        <MaybeTooltipOption
            week={value.week}
            year={value.year}
            tooltip={value.tooltip}
            mixpanelContentKey={value.mixpanelContentKey}
        >
            <Listbox.Button {...props} ref={ref}>
                {value.label}
            </Listbox.Button>
        </MaybeTooltipOption>
    );
});

const getLabelByRankType = ({ rankType, week, year }) => {
    if (rankType === RANK.TOTAL_POST) return 'All Time';
    if (rankType === RANK.AVG_WEEKLY)
        return week && year
            ? `Last Month (${getMonthRange({ week, year })})`
            : 'Last Month';
    if (rankType === RANK.LAST_WEEK)
        return week && year
            ? `Last Week (${getWeekRange({ week, year })})`
            : 'Last Week';
    throw new Error(`Invalid rank type: ${rankType}`);
};

const metricForRankType = (spec, rankType) => {
    const key = spec.metricKeys[rankType];
    if (!key) throw new Error(`Invalid rank type: ${rankType}`);
    return spec.metadata[key];
};

const getTooltipByRankType = (spec, { rankType }) => {
    const metric = metricForRankType(spec, rankType).getTableColumnConfig();
    return {
        tooltip: metric.tooltip,
        mixpanelContentKey: metric.mixpanelContentKey,
    };
};

const getOptionFromRankType = (spec, { rankType, week, year }) => {
    const label = getLabelByRankType({ rankType, week, year });
    const { tooltip, mixpanelContentKey } = getTooltipByRankType(spec, {
        rankType,
    });
    return {
        rankType,
        value: label,
        label,
        year,
        week,
        tooltip: { ...tooltip, title: label },
        mixpanelContentKey,
        apiRankType: spec.metricKeys[rankType],
    };
};

const getSortOptionFromRankType = (spec, { rankType }) =>
    metricForRankType(spec, rankType).getSortOption({ ascending: false });

const buildDefaultMetrics = (spec) => [
    [spec.metadata[spec.metricKeys[RANK.LAST_WEEK]], true],
    [spec.metadata[spec.metricKeys[RANK.TOTAL_POST]], false],
    [spec.metadata[spec.wowKey], false],
    [spec.metadata.create_timestamp, false],
];

// Inject the avg-weekly ("Last Month") column next to Views wherever that sort
// is offered, so it shows by default without each surface adding it to its own
// `metrics` list.
const withAvgWeeklyColumn = (spec, metrics, rankTypes) => {
    const avgWeeklyKey = spec.metricKeys[RANK.AVG_WEEKLY];
    if (
        !rankTypes.includes(RANK.AVG_WEEKLY) ||
        metrics.some(([metric]) => metric.key === avgWeeklyKey)
    ) {
        return metrics;
    }
    const lastWeekKey = spec.metricKeys[RANK.LAST_WEEK];
    const idx = metrics.findIndex(([metric]) => metric.key === lastWeekKey);
    const insertAt = idx >= 0 ? idx + 1 : metrics.length;
    const next = [...metrics];
    next.splice(insertAt, 0, [spec.metadata[avgWeeklyKey], true]);
    return next;
};

const DEFAULT_RANK_TYPES = [RANK.LAST_WEEK, RANK.AVG_WEEKLY, RANK.TOTAL_POST];

export const createTopPostsControls = (spec) => {
    const defaultMetrics = buildDefaultMetrics(spec);
    const handleMixpanelEvent = ({ rankType }) => ({
        rankType: spec.eventRankTypes[rankType],
    });

    return ({
        week,
        year,
        mixpanelContext,
        tableName,
        metrics = defaultMetrics,
        rankTypes = DEFAULT_RANK_TYPES,
        manageVisibleColumnsByRankType = true,
    }) => {
        // Default to the avg-weekly ("Last Month") option when it's included in
        // rankTypes; otherwise fall back to the first option in rankTypes.
        const [rankType, setRankType] = useState(
            rankTypes.includes(RANK.AVG_WEEKLY)
                ? RANK.AVG_WEEKLY
                : rankTypes[0],
        );
        const options = rankTypes.map((rankType) =>
            getOptionFromRankType(spec, { rankType, week, year }),
        );
        const value = options.find((option) => option.rankType === rankType);
        const defaultSortOption = getSortOptionFromRankType(spec, { rankType });
        const lastWeekKey = spec.metricKeys[RANK.LAST_WEEK];

        const {
            visibleOrderedMetrics,
            columnsMenu,
            setVisibleColumns,
            setColumnsOrder,
        } = useTableColumns({
            tableName,
            metrics: withAvgWeeklyColumn(spec, metrics, rankTypes),
            buttonProps: { size: 'default' },
            getOptionFromMetric: (metric) => {
                if (metric.key === lastWeekKey) {
                    // `rankTypes` is caller-supplied and need not include the
                    // last-week option, in which case there is no option to
                    // borrow a label from.
                    const lastWeekOption = options.find(
                        ({ rankType }) => rankType === RANK.LAST_WEEK,
                    );
                    if (lastWeekOption) {
                        return { key: metric.key, label: lastWeekOption.label };
                    }
                }
                return {
                    key: metric.key,
                    label: metric.getTableColumnConfig().label,
                };
            },
        });

        const onChange = (selected) => {
            setRankType(selected.rankType);
            if (!manageVisibleColumnsByRankType) return;

            // Whichever metric the new sort ranks by must be visible, or the
            // table sorts on a column the user cannot see.
            const promote =
                selected.rankType === RANK.TOTAL_POST ||
                selected.rankType === RANK.AVG_WEEKLY
                    ? spec.metricKeys[selected.rankType]
                    : lastWeekKey;
            if (visibleOrderedMetrics[0]?.key !== promote) {
                setVisibleColumns((currentColumns) =>
                    Array.from(new Set([...currentColumns, promote])),
                );
            }
        };

        const sortSelect = (
            <SortSelect
                options={options}
                value={value}
                onChange={onChange}
                mixpanelContext={mixpanelContext}
                handleMixpanelEvent={handleMixpanelEvent}
                SelectButton={SelectButton}
                SelectOption={SelectOption}
                buttonProps={{
                    as: IconButton,
                    IconRight: FiChevronDown,
                    size: 'default',
                }}
                {...(spec.menuAlign && { $menuAlign: spec.menuAlign })}
            />
        );

        return {
            sortSelect,
            value,
            options,
            defaultSortOption,
            visibleOrderedMetrics,
            columnsMenu,
            setVisibleColumns,
            setColumnsOrder,
        };
    };
};
